import { setData } from '@department-of-veterans-affairs/platform-forms-system/actions';
import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  parseUtmParams,
  readStoredUtmParams,
  storeUtmParams,
} from '../utils/queryParams';

/**
 * Capture UTM params from the URL, persist them to `sessionStorage`, and merge
 * them into form data as flat `utm*` keys so they are submitted with the form.
 *
 * Modeled on `useFormFeatureToggleSync`: it recomputes on every form-data
 * change and only writes keys that are not already present in form data, so it
 * re-applies the params if a save-in-progress prefill restore drops them, while
 * never overwriting values already in form data.
 *
 * @param {string} [search] - A `location.search` string (e.g. `?utm_source=x`).
 * @returns {void}
 */
export default function useUtmParamsSync(search) {
  const dispatch = useDispatch();
  const formData = useSelector(state => state?.form?.data ?? {});

  // Capture UTM params from the initial URL only; later URL changes are ignored.
  const utmParamsRef = useRef(parseUtmParams(search));

  // Persist the captured params so they survive later navigation (where the
  // query string is dropped) and hard reloads.
  useEffect(() => {
    storeUtmParams(utmParamsRef.current);
  }, []);

  // UTM keys (captured or previously stored) that are not yet present in form
  // data. Using the captured params avoids depending on the effect above having
  // already run.
  const updates = useMemo(() => {
    const source = { ...readStoredUtmParams(), ...utmParamsRef.current };

    return Object.entries(source).reduce((acc, [key, value]) => {
      if (formData[key] === undefined) {
        acc[key] = value;
      }

      return acc;
    }, {});
  }, [formData]);

  // Read the latest form data at dispatch time rather than the value captured
  // in the render closure, so a prefill update between render and effect does
  // not get overwritten with stale data.
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  useEffect(() => {
    if (Object.keys(updates).length > 0) {
      dispatch(setData({ ...formDataRef.current, ...updates }));
    }
  }, [updates, dispatch]);
}
